import React, {
    useCallback, useEffect, useMemo, useContext
} from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { AgentService, PolicyService } from 'gw-capability-policy';
import { ModalNextProvider } from '@jutro/components';
import { IntlContext } from '@jutro/locale';
import { error } from '@jutro/logger';
import { messages as commonMessages } from 'gw-platform-translations';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import DocumentsUploadComponent from 'tdic-capability-document-react';
import { withApplicationPropertyContext } from 'tdic-components-platform-react';
import { LocalDateUtil } from 'gw-portals-util-js';
import FileUploadViewerComponent from './FileUploadViewerComponent/FileUploadViewerComponent';
import metadata from './PolicyCommonDocuments.metadata.json5';
import messages from './PolicyCommonDocuments.messages';
import styles from './PolicyCommonDocuments.module.scss';

function PolicyCommonDocuments(props) {
    const {
        value: {
            documentDTOs: policyCommonDocuments, policyNumber, contacts, offeringCode
        },
        writableDocuments,
        documentTypes,
        updateWritableDocuments,
        policyCommonDetails,
        loggedUserContact
    } = props;
    const { authHeader } = useAuthentication();
    const intl = useContext(IntlContext);
    const publicDocuments = useMemo(() => {
        return _.filter(policyCommonDocuments, (document) => {
            return !document.isAuthor;
        });
    }, [policyCommonDocuments]);

    useEffect(() => {
        const writableDoc = _.filter(policyCommonDocuments, (document) => {
            return document.isAuthor;
        });
        updateWritableDocuments(writableDoc);
    }, [policyCommonDocuments, updateWritableDocuments]);

    const displayDocumentType = useCallback(
        (item) => {
            switch (item) {
                case 'Appeals':
                    return 'Appeal';
                case 'Final Audit':
                    return 'Audit';
                case 'Correspondence':
                    return 'Automatic Payment Withdrawl';
                case 'Cancellation Request':
                    return 'Cancellation Request';
                case 'Loss Run Request':
                    return 'Claim History Request';
                case 'Community Service Statement':
                    return 'Community Service Statement';
                case 'New Business Application':
                    return 'New Business Application';
                case 'Policy Changes':
                    return 'Policy Change';
                case 'Renewal Questionnaire':
                    return 'Renewal Questionnaire';
                case 'Surveys':
                    return 'Survey';
                default:
                    return '';
            }
        },
        []
    );

    const newDocumentType = useCallback(() => {
        const repeatedValues = [
            { displayName: 'Final Audit', code: 'finalaudit_tdic' },
            { displayName: 'Correspondence', code: 'correspondence_ext' },
            { displayName: 'Other', code: 'other_ext' }
        ];
        const documentList = [];
        if (!_.isEmpty(documentTypes)) {
            documentTypes.forEach((data) => {
                const display = {
                    displayName: displayDocumentType(data.displayName),
                    code: data.displayName
                };
                documentList.push(display);
            });
        }
        return documentList.concat(repeatedValues);
    }, [documentTypes, displayDocumentType]);

    const deleteDocument = useCallback(
        (item) => {
            if (!item.canDelete) {
                return;
            }

            const { publicID } = item;
            ModalNextProvider.showConfirm({
                title: messages.removeDocument,
                message: messages.confirmRemoveDocument,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.documentRemoveConfirmYes,
                cancelButtonText: messages.documentRemoveConfirmNo
            }).then(async (results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                try {
                    const isDocumentDeleted = await PolicyService.removeDocument(
                        publicID,
                        authHeader
                    );
                    if (isDocumentDeleted) {
                        const newDocuments = _.filter(writableDocuments, (doc) => {
                            return doc.workingPublicID !== publicID;
                        });
                        updateWritableDocuments(newDocuments);
                    }
                } catch (documentDeletionError) {
                    error(`PolicyCommonDocuments: deleteDocument - Unable to delete documents ${JSON.stringify(documentDeletionError)}`);
                    ModalNextProvider.showAlert({
                        title: messages.documentDeletionFailed,
                        message: messages.documentCannotDeleted,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).catch(_.noop);
                }
                return true;
            }, _.noop);
        },
        [authHeader, writableDocuments, updateWritableDocuments]
    );

    const handleError = useCallback((err) => {
        error(`PolicyCommonDocuments: handleError - Unable to upload documents ${err}`);
        return (ModalNextProvider.showAlert({
            title: messages.documentUploadFailed,
            message: messages.documentCannotUploaded,
            status: 'error',
            confirmButtonText: commonMessages.ok
        }));
    }, []);

    // eslint-disable-next-line consistent-return
    const uploadDocument = useCallback((fileData, docType, docDesc) => {
        const namedInsuredContact = contacts.find((item) => {
            return item.contactRole === 'Named Insured';
        });
        const primaryName = _.get(namedInsuredContact, 'contact.displayName', '');

        const documentMetaDataTemplate = {
            mimeType: fileData.type,
            documentName: fileData.name,
            typeOfDocument: docType === 'other_ext' ? 'correspondence_ext' : docType,
            description: docDesc,
            policyNumber: policyNumber,
            policyHolder: primaryName,
            requestedBy: _.get(loggedUserContact, 'accountContact.displayName', ''),
            dateUploaded: LocalDateUtil.today()
        };
        try {
            AgentService.sendUploadDocumentEmailToAgent(
                fileData,
                documentMetaDataTemplate,
                authHeader
            ).then((response) => {
                if (response) {
                    return (ModalNextProvider.showAlert({
                        title: messages.documentUploadedTitle,
                        message: messages.documentUploaded,
                        status: 'error',
                        confirmButtonText: commonMessages.ok
                    }).catch(_.noop));
                }
                return handleError();
            });
        } catch (err) {
            error(`PolicyCommonDocuments: uploadDocument - Unable to upload documents ${JSON.stringify(err)}`);
            return handleError();
        }
    }, [authHeader, contacts, handleError, intl, loggedUserContact, offeringCode, policyNumber]);

    const onViewDownloadClicked = useCallback((document) => {
        const { publicID, sessionID } = document;
        PolicyService.downloadDocumentLink(publicID, sessionID)
            .then((response) => {
                if (response.ok) {
                    response.text().then((text) => {
                        const docWin = window.open('', '_blank');
                        docWin.document.write(text);
                    });
                } else {
                    ModalNextProvider.showAlert({
                        title: messages.viewDownload,
                        message: messages.downloadErrorMessage,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).then(_.noop);
                }
            });
    }, []);

    const paperlessData = useCallback(() => {
        const paperlessValue = _.get(policyCommonDetails, 'currentPeriodDetails.paperlessPreference', false);
        return paperlessValue ? 'Yes' : 'No';
    }, [policyCommonDetails]);

    const overrides = {
        documentDeliveryData: {
            isReadOnly: true,
            value: paperlessData()
        },
        policyDocuments: {
            visible: !_.isEmpty(publicDocuments)
        },
        readOnlyDocumentFileUploadComponent: {
            data: {
                isReadOnly: true,
                documents: publicDocuments
            },
            getDownloadLink: onViewDownloadClicked
        },
        documentsComponent: {
            docTypeList: newDocumentType()
        }
    };

    const resolvers = {
        resolveComponentMap: {
            documentsComponent: FileUploadViewerComponent,
            uploadDocumentsComponent: DocumentsUploadComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onDocumentDelete: deleteDocument,
            onDocumentUpload: uploadDocument,
            onViewDownloadClicked,
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(
                metadata.componentContent,
                policyCommonDocuments,
                id,
                path,
                overrides
            );
        },
        [policyCommonDocuments, overrides]
    );

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={policyCommonDocuments}
            overrideProps={overrides}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
            resolveValue={readValue}
        />
    );
}

PolicyCommonDocuments.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    value: PropTypes.shape({
        documentDTOs: PropTypes.arrayOf(PropTypes.shape({})),
        policyNumber: PropTypes.string,
        canUploadDocument: PropTypes.bool,
        contacts: PropTypes.arrayOf(PropTypes.shape({
            contact: PropTypes.shape({
                paperlessDelivery_Ext: PropTypes.bool
            })
        })),
        productName: PropTypes.string,
        offeringCode: PropTypes.string.isRequired
    }),
    writableDocuments: PropTypes.shape([]).isRequired,
    updateWritableDocuments: PropTypes.shape([]).isRequired,
    documentTypes: PropTypes.shape([]).isRequired,
    policyCommonDetails: PropTypes.shape([]).isRequired,
    loggedUserContact: PropTypes.shape([]).isRequired
};

PolicyCommonDocuments.defaultProps = {
    value: {
        documentDTOs: [],
        policyNumber: '',
        canUploadDocument: false,
        contacts: []
    }
};

export default withRouter(withApplicationPropertyContext(PolicyCommonDocuments));
